import { FC, useContext, useEffect, useLayoutEffect, useReducer, useState } from "react";

import AuthSubHeader from "src/components/authSubHeader";
import { LoaderContext } from "src/context/loaderContext";

import { EOBs_TABLE_HEADER } from "src/constants/cases";

import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import ReactPaginate from "react-paginate";
import { Link } from "react-router-dom";
import { EOBsAPIResponse, EOBsReportAPIResponse, QueryParamsState, QueryStateActions, QueryStateActionType } from "./types";
import EmploymentOfBenefitFilters from "src/components/EOBsFilter";
import { initialState, queryParamReducer } from "./reducer";
import { AuthContext } from "src/context/authContext";
import { format, parse } from "date-fns";
import AuthMaunfacturerSubHeader from "src/components/manufacturer/authManufacturerSubHeader";
import UploadEOBs from "src/components/uploadEOBs";
import ViewPdf from "src/components/viewPdf";
import { exportToExcelEOBs, formatInsuranceType } from "src/helpers/downloadFile";
import { formatFixedWithCommas } from "../../helpers";
import FooterText from "src/components/footerText";


const EOBsListing: FC = (): JSX.Element => {
  const [queryParams, dispatch] = useReducer(queryParamReducer, initialState);
  const { setLoader, hideLoader } = useContext(LoaderContext);
  const { isManufacturer } = useContext(AuthContext);
  const [showUpload, setShowUpload] = useState<boolean>();
  const [showPdf, setShowPdf] = useState<boolean>(false);
  const [selectedPdfId, setSelectedPdfId] = useState<number>();
  const [Queries, setUpdatedQueries] = useState<QueryParamsState>(queryParams);

  const [isExportingInProgress, setExportingInProgressStatus] = useState<boolean>(false);


  useLayoutEffect(() => {
    setLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const { error: eobsReportError, data: eobsReportListing, mutate: getEOBsReport } =
    useFetch<EOBsReportAPIResponse>(APIEndpoints.FETCH_EOBs_REPORTS, queryParams, {}, false);

  useEffect(() => {
    if (eobsReportListing) {
      hideLoader();
      setExportingInProgressStatus(false)
      dispatch({
        type: QueryStateActions.CHANGE_PAGINATION_BIT,
        payload: true,
      })
      exportToExcelEOBs(eobsReportListing?.data, "EOBs Report")
      setExportingInProgressStatus(false)
    } else {
      hideLoader();
      setExportingInProgressStatus(false)
      dispatch({
        type: QueryStateActions.CHANGE_PAGINATION_BIT,
        payload: true,
      })
      setExportingInProgressStatus(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eobsReportListing, eobsReportError])

  const { error: eobsError, data: eobsListing, mutate: getEOBsListing, removeErrorAndData: clearEOBsListingState } =
    useFetch<EOBsAPIResponse>(APIEndpoints.EOBs_LISTING, queryParams);

  useEffect(() => {
    if (eobsListing || eobsError)
      hideLoader();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eobsListing, eobsError])

  const downloadReportEOB = () => {
    setExportingInProgressStatus(true)
    dispatch({
      type: QueryStateActions.CHANGE_PAGINATION_BIT,
      payload: false,
    })
    getEOBsReport({}, {}, { ...Queries, pagination: false })
  }

  const handleSearchWithQueryParam = (updatedState?: QueryParamsState) => {
    updatedState && setUpdatedQueries(updatedState)
    if (eobsError || eobsListing) clearEOBsListingState();
    setLoader();
    getEOBsListing(null, null, updatedState ? updatedState : queryParams);
  };

  const onClearButtonClicked = () => {
    dispatch({
      type: QueryStateActions.CLEAR_STATE,
    });
    setUpdatedQueries(queryParams)
    getEOBsListing(null, null, initialState);
  };

  const changeRecordOrderByColumn = (value: string): void => {
    const order = Queries?.direction === 'desc' ? 'asc' : 'desc';
    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_SORT_PARAMS,
      payload: {
        direction: queryParams?.sort_by === value ? order : "desc",
        sort_by: value,
      },
    };
    dispatch(action);
    if (Queries) {
      const updatedState = queryParamReducer(Queries, action);
      handleSearchWithQueryParam(updatedState);
    }
  };

  const changePageNumber = (page: { selected: number }): void => {
    const action: QueryStateActionType = {
      type: QueryStateActions.CHANGE_PAGE,
      payload: page.selected + 1,
    };
    dispatch(action);

    const updatedState = queryParamReducer(queryParams, action);
    handleSearchWithQueryParam(updatedState);
  };

  const updateQueryParamsAndCallAPI = (
    updatedQueries: QueryParamsState,
    isReset: boolean = false
  ): void => {

    setLoader();
    handleSearchWithQueryParam(updatedQueries)
  };


    return (
        <div className="overflow-y-auto overflow-x-hidden d-flex flex-column flex-fill">
            <div className="bg-mist py-2">
                <div className="container-fluid">
                    {isManufacturer ? <AuthMaunfacturerSubHeader/> : <AuthSubHeader/>}
                </div>
            </div>
            <div className="container-fluid pt-2 d-flex flex-column flex-fill">
                <div className="row align-items-center">
                    <div className="col-12 col-md-9">
                        <h2 className="m-0 page-heading">EOB Warehouse</h2>
                        <p>
                            {`The following is a list of de-identifiable EOBs which may be viewed. To filter this list, simply select one or more values from the dropdowns below and click 'Find'.`}<br/>
                            {`Please note, reimbursement amounts may vary based on each individual provider's contract with the payer.`}
                        </p>
                    </div>
                    <div className="col-12 col-md-3 text-end mb-3 mb-md-0">
                        {!isManufacturer ?
                            <button type="button" className="btn btn-primary"
                                    onClick={() => setShowUpload(true)}
                            >
                                Upload EOB
                            </button>
                            :
                            (isExportingInProgress ? (
                                        <div className="col">
                                            <p className="text-dark h5">
                                                <span className="spinner-border spinner-border-sm"
                                                      style={{width: 20, height: 20}} aria-hidden="true"></span>{' '}
                                                <i>Fetching EOBs Report</i>
                                            </p>
                                        </div>
                                    )
                                    : (
                                        <button type="button" className="btn btn-primary" onClick={downloadReportEOB}>
                                            Download Report
                                        </button>
                                    )
                            )}
                    </div>
                </div>
                <div className="row g-2">
                    <EmploymentOfBenefitFilters
                        state={queryParams}
                        dispatch={dispatch}
                        defaultQueryParams={queryParams}
                        handleSearchWithQueryParam={handleSearchWithQueryParam}
                        updateQueryParams={updateQueryParamsAndCallAPI}
                        onClearButtonClicked={onClearButtonClicked}
                    />
                </div>
                <div className="card mt-3 d-flex flex-fill">
                    <div className="card-body p-0 d-flex flex-column">
                        <div className="table-responsive-sm table-responsive-md table-responsive-lg flex-fill">
                            <table className="table table-striped table-sm m-0">
                                <thead>
                                <tr className="text-center">
                                    {EOBs_TABLE_HEADER.map((tableHeader, index) => (
                                        <th
                                            key={index}
                                            className={`${tableHeader.label.trim().startsWith('$') ? '' : ''
                                            }`}
                                            onClick={tableHeader.value !== '' ? () => changeRecordOrderByColumn(tableHeader.value) : undefined}
                                        >
                      <span
                          role={tableHeader.value === '' ? "textbox" : "button"}
                          className={
                              tableHeader.value === ''
                                  ? ''
                                  : ''
                          }
                      >
                        {tableHeader.label}
                          {queryParams.sort_by === tableHeader.value ? (
                              <i
                                  className={`bi ${queryParams.direction === 'desc' ? 'bi-caret-down-fill' : 'bi-caret-up-fill'
                                  }`}
                              ></i>
                          ) : null}
                      </span>
                                        </th>
                                    ))}
                                </tr>
                                </thead>

                                <tbody>
                                {eobsListing && eobsListing.data && eobsListing.data && eobsListing.data.length > 0 ?
                                    eobsListing.data.map((record) => (
                                        <tr key={record.id} className="align-middle text-center">
                                            <td className="case-td">
                                                <Link
                                                    to={{pathname: `/EOBs/detail/${record.id}`}}>
                                                    {record.id}
                                                </Link>
                                            </td>
                                            <td>{record && record.claim_date ? format(parse(record.claim_date.split("T")[0], "yyyy-MM-dd", new Date()), "MM/dd/yyyy") : ''}</td>
                                            <td>{record ? record.product : ''}</td>
                                            <td>{record ? record.soc : ''}</td>
                                            <td>{record ? record.insurance_state : ''}</td>
                                            <td>{record ? record.insurance_name : ''}</td>
                                            <td>{record ? record.insurance_type && formatInsuranceType(record.insurance_type) : ''}</td>
                                            <td>{record ? record.insurance_mac : ''}</td>
                                            <td>{record && record.cpt_codes && record.cpt_codes.billed && record.cpt_codes.billed >= 1 ? formatFixedWithCommas(record.cpt_codes.billed) : ""}</td>
                                            <td>{record && record.cpt_codes && record.cpt_codes.allowed && record.cpt_codes.allowed >= 1 ? formatFixedWithCommas(record.cpt_codes.allowed) : ""}</td>
                                            <td>{record && record.cpt_codes && record.cpt_codes.paid && record.cpt_codes.paid >= 1 ? formatFixedWithCommas(record.cpt_codes.paid) : ""}</td>
                                            <td>{record && record.cpt_codes && record.cpt_codes.copay && record.cpt_codes.copay >= 1 ? formatFixedWithCommas(record.cpt_codes.copay) : ""}</td>
                                            <td>

                                                {record && record.redacted_file &&
                                                    <button
                                                        className="btn text-link"
                                                        onClick={() => {
                                                            setSelectedPdfId(record.id);
                                                            setShowPdf(true);
                                                        }}>View
                                                    </button>
                                                }
                                            </td>
                                            <td className="fs-6">{record && record.created_date ? format(new Date(record.created_date), "MM/dd/yyyy") : ''}</td>
                                        </tr>
                                    )) : null
                                }
                                </tbody>
                            </table>
                        </div>

                        {eobsListing && eobsListing.total_pages ? (
                            <ul className="pagination-nav sticky-bottom bg-white m-0">
                                {queryParams.page !== 1 ? (
                                    <li className="page-item">
                                        <button className="nxt-lst btn" onClick={() => changePageNumber({selected: 0})}>
                                            <i className="bi bi-chevron-double-left"></i> First
                                        </button>
                                    </li>
                                ) : null}

                                <li>
                                    <ReactPaginate
                                        nextLabel={<>Next <i className="bi bi-chevron-right"></i></>}
                                        onPageChange={changePageNumber}
                                        pageRangeDisplayed={3}
                                        marginPagesDisplayed={2}
                                        pageCount={eobsListing.total_pages}
                                        previousLabel={<><i className="bi bi-chevron-left"></i> Previous</>}
                                        pageClassName="page-item"
                                        pageLinkClassName="page-link"
                                        previousClassName="page-item"
                                        previousLinkClassName="page-link"
                                        nextClassName="page-item"
                                        nextLinkClassName="page-link"
                                        breakLabel="..."
                                        breakClassName="page-item"
                                        breakLinkClassName="page-link"
                                        containerClassName="pagination"
                                        activeClassName="active"
                                        renderOnZeroPageCount={null}
                                        forcePage={queryParams.page - 1}
                                    />
                                </li>

                                {queryParams.page !== eobsListing.total_pages ? (
                                    <li className={`nxt-lst page-item ${queryParams.page === eobsListing.total_pages ? 'disabled' : ''}`}>
                                        <button className="btn" onClick={() => changePageNumber({selected: eobsListing.total_pages - 1})}>
                                            Last <i className="bi bi-chevron-double-right"></i>
                                        </button>
                                    </li>
                                ) : null}
                            </ul>
                        ) : null}
                    </div>
                </div>
                {showUpload &&
                    <div>
                        <UploadEOBs onClose={() => setShowUpload(false)}/>
                    </div>
                }
                {showPdf && selectedPdfId && (
                    <ViewPdf id={selectedPdfId} onClose={() => {
                        setShowPdf(false);
                        setSelectedPdfId(0);
                    }}/>
                )}

            </div>
            {!isManufacturer && <FooterText footerType="black"/>}
        </div>
    )
}
export default EOBsListing;
